import { lazy, Suspense } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate
} from "react-router-dom-v5-compat";

import loaderWithRetry from "seneca-common/utils/loaderWithRetry";

import TransferSubscriptionModal from "features/parents/pages/settings/components/TransferSubscriptionModal";
import { MODAL_ROUTES } from "routing/routes";

const ProgressReportModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "progress-report-modal-chunk" */
        "features/teachers/pages/reports/progress-report-modal/components/ProgressReportModal"
      )
  )
);

const ShareAssignmentModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "share-assignment-chunk" */
        "features/teachers/pages/assignment/modals/AssignmentShareModal"
      )
  )
);

const ManageStudentModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "manage-student-chunk" */
        "features/teachers/common/components/ManageStudentModal/ManageStudentModal"
      )
  )
);

const AssignmentStudentResultModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "assignment-student-result-chunk" */
        "features/teachers/pages/assignment/modals/AssignmentStudentResultModal"
      )
  )
);

const DeleteClassAssignmentModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "delete-assignment-chunk" */
        "features/teachers/pages/assignment/modals/DeleteClassAssignment/DeleteClassAssignmentModal"
      )
  )
);

const DeleteClassAssignmentsModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "delete-assignments-chunk" */
        "features/teachers/pages/assignment/modals/DeleteClassAssignments/DeleteClassAssignmentsModal"
      )
  )
);

const EditMultiClassAssignmentModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "edit-multi-class-assignment-chunk" */
        "features/teachers/pages/assignment/modals/EditMultiClassAssignment/EditMultiClassAssignment"
      )
  )
);

const PreSpeedyMarkingModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "pre-speedy-marking-chunk" */
        "features/teachers/pages/assignment/modals/PreSpeedyMarkingModal/PreSpeedyMarkingModal"
      )
  )
);

const MarkingModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "marking-chunk" */
        "features/teachers/pages/assignment/modals/MarkingModal/MarkingModal"
      )
  )
);

const SectionSelectorModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "section-selector-chunk" */
        "features/teachers/features/insights/components/SectionSelectorModal"
      )
  )
);

const MISClassTriggerModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "mis-class-trigger-chunk" */
        "features/teachers/pages/classes-page/modals/MISClassTrigger/MISClassTriggerModal"
      )
  )
);

const RemoveCourseModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "remove-course-chunk" */
        "features/teachers/pages/class-page/common/modals/RemoveCourseModal/RemoveCourseModal"
      )
  )
);

const RegionSelectionModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "region-selection-chunk" */
        "features/regions/common/components/RegionSelectionModal"
      )
  )
);

const InviteParentModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "invite-parent-chunk" */
        "features/dashboard/pages/components/NavbarParentInviteModal/NavbarParentInviteModal"
      )
  )
);

const InviteParentInfoModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "invite-parent-info-chunk" */
        "features/teachers/common/components/InviteParentInfoModal"
      )
  )
);

const InviteClassesParentsModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "invite-classes-parents-chunk" */
        "features/teachers/common/components/InviteClassesParentsModal"
      )
  )
);

const ChangeEmailModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "change-email-chunk" */
        "features/settings/features/personal-information/features/change-email/ChangeEmailModal"
      )
  )
);

const DeleteClassModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "delete-class-chunk" */
        "features/teachers/pages/class-settings/modals/DeleteClassModal"
      )
  )
);

const LeaveClassModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "leave-class-chunk" */
        "features/teachers/pages/class-settings/modals/LeaveClassModal"
      )
  )
);

const ArchiveClassesModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "archive-classes-chunk" */
        "features/teachers/common/modals/Archive/ArchiveClassesModal"
      )
  )
);

const HideClassesModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "hide-classes-chunk" */
        "features/teachers/common/modals/HideClasses/HideClassesModal"
      )
  )
);

const UnhideClassesModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "unhide-classes-chunk" */
        "features/teachers/common/modals/UnhideClasses/UnhideClassesModal"
      )
  )
);

const JoinClassModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "join-class-chunk" */
        "features/dashboard/pages/classes/components/JoinClassModal"
      )
  )
);

const InviteTeacherModal = lazy(() =>
  // @ts-ignore
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "invite-teacher-chunk" */
        "features/dashboard/pages/classes/components/InviteTeacherModal"
      )
  )
);

const ClassDoesNotExistModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "class-does-not-exist-chunk" */
        "features/teachers/pages/classes-page/modals/ClassDoesNotExist"
      )
  )
);

const ManageTeachersModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "manage-teachers-chunk" */
        "features/teachers/features/class-sharing/components/ManageTeachersModal"
      )
  )
);

const TransferOwnershipModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "transfer-ownership-chunk" */
        "features/teachers/features/class-sharing/components/TransferOwnershipModal"
      )
  )
);

const RemoveTeacherModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "remove-teacher-chunk" */
        "features/teachers/features/class-sharing/components/RemoveTeacherModal"
      )
  )
);

const ResetPasswordsSchoolModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "reset-passwords-school-chunk" */
        "features/teachers/pages/school/components/ResetPasswordsModal"
      )
  )
);

const JoinSchoolClassModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "join-school-class-chunk" */
        "features/teachers/pages/classes-page/modals/JoinClass/JoinClassModal"
      )
  )
);

const JoinSchoolClassesModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "join-school-classes-chunk" */
        "features/teachers/pages/classes-page/modals/JoinClassesModal/JoinClassesModal"
      )
  )
);

const ClassStudentInviteSuccessModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "class-student-invite-success-chunk" */
        "features/teachers/modals/parents-invite/invite-all-school-parents/modals/SuccessModal"
      )
  )
);

const InviteOtherClassesModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "invite-other-classes-chunk" */
        "features/teachers/modals/parents-invite/invite-all-school-parents/modals/InviteOtherClassesParentsModal"
      )
  )
);

const SchoolRenewalRequestSuccessModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "school-renewal-success-chunk" */
        "features/teachers/features/gating/components/TeacherRenewalRequestSuccessModal"
      )
  )
);

const TeacherPlatformGatingModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "teacher-platform-gating-chunk" */
        "features/teachers/features/gating/TeacherPlatformGatingModal"
      )
  )
);

const ReviewMultiClassAssignmentMarkingModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "review-multi-class-assignment-marking-chunk" */
        "features/teachers/features/assignment/modals/ReviewMultiClassAssignmentMarkingModal"
      )
  )
);

const ShareSectionModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "share-modal-chunk" */
        "features/classroom/pages/section-homepage/modals/share-modal"
      )
  )
);

const SectionHomepageQuizModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "section-homepage-quiz-modal-route" */
        "features/classroom/pages/section-homepage/modals/quiz/QuizModalPremium"
      )
  )
);

const WrongAnswersTrialModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "wrong-answers-trial-route" */
        "features/classroom/pages/section-homepage/modals/wrong-answers-trial"
      )
  )
);

const GetWrongAnswersModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "get-wrong-answers-modal-route" */
        "features/classroom/pages/section-homepage/modals/wrong-answers-get-premium/WrongAnswersModalPremium"
      )
  )
);

const DownloadNotesModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "download-notes-route" */
        "features/classroom/pages/section-homepage/components/SectionHomepage/components/DownloadNotesModal"
      )
  )
);

const SectionPreviewModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "section-preview-chunk" */
        "features/teachers/features/assignment-journey/components/common/SectionPreviewModal"
      )
  )
);

const WordPreviewModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "word-preview-chunk" */
        "features/teachers/features/assignment-journey/components/common/WordPreviewModal"
      )
  )
);

const PremiumCoursesModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "premium-courses-route" */
        "features/teachers/modals/premium-courses/PremiumCoursesModal"
      )
  )
);

const AddCourseModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "add-course-modal-route" */
        "features/teachers/modals/add-course/AddCourseModal"
      )
  )
);

const QuizDeletedModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "quiz-deleted-route" */
        "features/teachers/pages/quizzes/components/QuizPage/components/QuizModals/QuizDeletedModal"
      )
  )
);

const QuizAddImageModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "quiz-add-image-route" */
        "features/teachers/pages/quizzes/components/QuizPage/components/QuizModals/QuizAddImageModal/QuizAddImageModal"
      )
  )
);

const QuizPremiumTrigger = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "quiz-premium-trigger-route" */
        "features/teachers/pages/quizzes/components/QuizPage/components/QuizModals/QuizPremiumTrigger"
      )
  )
);

const QuizShareSyncedGatingModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "share-synced-gating-modal-route" */
        "features/teachers/pages/quizzes/components/common/QuizShareSyncedGatingModal"
      )
  )
);

const ShareQuizzesWithTeachersModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "share-quizzes-with-teachers-modal-route" */
        "features/teachers/pages/quizzes/components/QuizzesPage/common/ShareQuizzesWithTeachersModal/ShareQuizzesWithTeachersModal"
      )
  )
);

const ChildSwitcherModal = lazy(() =>
  loaderWithRetry(
    () =>
      /* webpackChunkName: "child-switcher-modal-route" */
      import(
        "features/parents/features/child-switcher/components/ChildSwitcherModal"
      )
  )
);

const ParentInviteChildrenModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "parent-invite-children-modal-route" */
        "features/parents/pages/monitoring-overview/components/InviteChildrenModal/InviteChildrenModal"
      )
  )
);

const ViewInviteModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "view-invite-modal-route" */
        "features/teachers/features/class-sharing/components/ViewInviteModal"
      )
  )
);

const UnarchiveClassesModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "unarchive-classes-modal-route" */
        "features/teachers/pages/classes-page/modals/Unarchive/UnarchiveClassesModal"
      )
  )
);

const CreateClassModalContainer = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "create-class-modal-route" */
        "features/teachers/pages/classes-page/modals/CreateClass/CreateClassModalContainerComp"
      )
  )
);

const InviteStudentsQrCodeModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "invite-students-qr-code-modal-route" */
        "features/teachers/pages/invite-students/InviteStudentsQrCode"
      )
  )
);

const EndQuizSessionModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "end-quiz-session-modal-route" */
        "features/classroom/pages/quiz-session/modals/EndQuizSessionModal"
      )
  )
);

const EndQuizSessionAnimation = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "end-quiz-session-animation-route" */
        "features/classroom/pages/quiz-session/modals/EndQuizSessionAnimation"
      )
  )
);

const QuizSessionAssignmentCompleteModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "quiz-session-assignment-complete-modal-route" */
        "features/classroom/pages/quiz-session/modals/QuizSessionAssignmentComplete"
      )
  )
);

const QuitQuizSessionModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "quit-quiz-session-modal-route" */
        "features/classroom/pages/quiz-session/modals/QuitQuizSessionModal"
      )
  )
);

const QuitSessionModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "quit-session-modal-route" */
        "features/classroom/pages/classroom/components/QuitSessionModal/QuitSessionModal"
      )
  )
);

const EndVocabularySessionModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "end-vocabulary-session-modal-route" */
        "features/classroom/pages/vocabulary/modals/EndVocabularySessionModal/EndVocabularySessionModal"
      )
  )
);

const EndVocabularySessionSummaryModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "end-vocabulary-session-summary-modal-route" */
        "features/classroom/pages/vocabulary/modals/EndVocabularySessionSummaryModal/EndVocabularySessionSummaryModal"
      )
  )
);

const VocabularyAssignmentProgressModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "vocabulary-assignment-progress-modal-route" */
        "features/classroom/pages/vocabulary/modals/VocabularyAssignmentProgressModal/VocabularyAssignmentProgressModal"
      )
  )
);

const VocabularyAssignmentCompleteModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "vocabulary-assignment-complete-modal-route" */
        "features/classroom/pages/vocabulary/modals/VocabularyAssignmentCompleteModal/VocabularyAssignmentCompleteModal"
      )
  )
);

const InviteParentsForExamQuestionsModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "invite-parents-for-exam-questions-modal" */
        "features/classroom/pages/course-homepage/components/CourseHomepage/components/InviteParentsForExamQuestionsModal/InviteParentsForExamQuestionsModal"
      )
  )
);

const InviteParentsForWrongAnswersModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "invite-parents-for-wrong-answers-modal" */
        "features/classroom/pages/section-homepage/modals/wrong-answers-invite-parents/InviteParentsForWrongAnswersModal"
      )
  )
);

const ParentPlatformPreviewModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "parent-platform-preview-route" */
        "features/teachers/common/components/ParentPlatformPreviewModal"
      )
  )
);

const AskAmeliaGatingModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "amelia-section-gating-route" */
        "features/classroom/pages/section-homepage/modals/ask-amelia/AskAmeliaGatingModal"
      )
  )
);

const WrongAnswersGatingModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "wrong-answers-gating-route" */
        "features/classroom/pages/multi-section-session/pages/MultiSectionResults/components/WrongAnswersGatingModal"
      )
  )
);

const QuizPreviewModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "quiz-preview-route" */
        "features/teachers/pages/quizzes/components/common/QuizPreviewModal/QuizPreviewModal"
      )
  )
);

const AssignUnpublishedQuizModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "assign-unpublished-quiz-route" */
        "features/teachers/pages/quizzes/components/QuizPage/components/QuizModals/AssignUnpublishedQuizModal"
      )
  )
);

const DeleteQuizModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "delete-quiz-route" */
        "features/teachers/pages/quizzes/components/QuizPage/components/QuizModals/DeleteQuizModal"
      )
  )
);

const ParentMarketingModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "parent-marketing-modal-route" */
        "features/parents/modals/parent-marketing/ParentMarketingModal"
      )
  )
);

const ClaimTutoringTrialModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "claim-tutoring-trial-modal-route" */
        "features/parents/modals/parent-marketing/ClaimTutoringTrialModal"
      )
  )
);

const CompleteTeacherAccountModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "complete-teacher-account-modal" */
        "features/sign-up/complete-account/CompleteTeacherAccountPage"
      )
  )
);

const FeedbackModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "feedback-modal-route" */
        "features/feedback/components/GeneralFeedbackModal"
      )
  )
);

const MISInfoModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "mis-info-modal-route" */
        "features/teachers/pages/homepage/components/MiSInfoModal/MiSInfoModal"
      )
  )
);

const UnmanagedUserInSyncedSchoolModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "unmanaged-user-in-synced-school-modal-route" */
        "features/teachers/pages/homepage/components/UnmanagedUserInSyncedSchoolTrigger/UnmanagedUserInSyncedSchoolModal"
      )
  )
);

const PlatformDemoModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "platform-demo-modal" */
        "features/teachers/modals/parents-invite/invite-all-school-parents/modals/PlatformDemoModal"
      )
  )
);

const DataProcessingModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "data-processing-modal" */
        "features/teachers/modals/parents-invite/invite-all-school-parents/modals/DataProcessingModal"
      )
  )
);

const SelectYearGroupsModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "select-year-groups-modal" */
        "features/teachers/modals/parents-invite/invite-all-school-parents/modals/SelectYearGroupsModal"
      )
  )
);

const InviteParentsPreviewModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "invite-parents-preview-modal" */
        "features/teachers/modals/parents-invite/invite-all-school-parents/modals/InviteParentsPreviewModal"
      )
  )
);

const SelectClassesInviteParentsModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "select-classes-invite-parents-modal" */
        "features/teachers/features/assignment-journey/components/steps/AssignmentInviteParentsPage/SelectClassesModal"
      )
  )
);

const ActivateAssignmentNotificationsModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "activate-assignment-notifications-modal" */
        "features/notifications/components/activation/ActivateAssignmentNotifications/ActivateAssignmentNotificationsModal"
      )
  )
);

const TutoringFormModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "tutoring-form-modal" */
        "features/parents/features/tutoring-form-modal/components/TutoringFormModal"
      )
  )
);

const ShareResourceModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "share-resource-modal" */
        "features/teachers/pages/resource-hub/components/Modals/ShareResourceModal/ShareResourceModal"
      )
  )
);

const ParentAutoInvitesPreviewModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "parent-auto-invites-preview-modal" */
        "features/teachers/features/parent-auto-invites/ParentAutoInvitesPreviewModal"
      )
  )
);

const ParentAutoInvitesSelectYearGroupModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "parent-auto-invites-select-year-group-modal" */
        "features/teachers/features/parent-auto-invites/ParentAutoInvitesSelectYearGroupModal"
      )
  )
);

const ParentAutoInvitesSuccessModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "parent-auto-invites-success-modal" */
        "features/teachers/features/parent-auto-invites/ParentAutoInvitesSuccessModal"
      )
  )
);

const HardestQuestionsGatingModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "hardest-questions-gating-modal" */
        "features/classroom/pages/session/features/message-types/select-session-type/SelectSessionType/components/HardestQuestionsGatingModal/HardestQuestionsGatingModal"
      )
  )
);

const HardestQuestionsTeacherGatingModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "hardest-questions-teacher-gating-modal" */
        "features/teachers/features/sections-selector/components/HardestQuestionsGatingModal"
      )
  )
);

const UnlockMultiClassAssignmentsModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "unlock-multi-class-assignments-chunk" */
        "features/teachers/features/assignment-journey/components/steps/AssignmentSelectClassPage/components/UnlockMultiClassAssignmentsModal"
      )
  )
);

const GenerateCustomReportModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "generate-custom-report-chunk" */
        "features/teachers/pages/reports/modals/GenerateCustomReportModal/GenerateCustomReportModal"
      )
  )
);

const CustomReportTypeStepModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "generate-custom-report-type-chunk" */
        "features/teachers/pages/reports/modals/GenerateCustomReportModal/CustomReportTypeStepModal"
      )
  )
);

const CustomReportDetailsStepModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "generate-custom-report-details-chunk" */
        "features/teachers/pages/reports/modals/GenerateCustomReportModal/CustomReportDetailsStepModal"
      )
  )
);

const UpdateAppModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "update-app-modal-chunk" */
        "seneca-common/features/update-app-modal/UpdateAppModal"
      )
  )
);

const ConfirmDeleteStudentsModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "confirm-delete-students-chunk" */
        "features/teachers/pages/class-students/components/ConfirmDeleteStudentsModal/ConfirmDeleteStudentsModal"
      )
  )
);

const ExamQuestionsOnlyInformation = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "exam-questions-only-information-chunk" */
        "features/teachers/features/assignment-journey/components/steps/AssignmentSelectTypePage/components/ExamQuestionsOnlyInformation"
      )
  )
);

const MagicQuizzesInfoModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "magic-quizzes-info-modal-chunk" */
        "features/teachers/features/assignment-journey/components/steps/AssignmentSelectTypePage/components/MagicQuizzesInfoModal"
      )
  )
);

const AuthenticationModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "authentication-page-chunk" */
        "./features/subscriptions/features/upgrades/components/AuthenticationModal"
      )
  )
);

const PaymentSuccessfulModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "payment-success-page-chunk" */
        "./features/subscriptions/features/upgrades/components/PaymentSuccessfulModal"
      )
  )
);

const RetrySubscriptionPaymentModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "retry-subscription-payment-page-chunk" */
        "./features/subscriptions/features/upgrades/components/RetryPaymentModal"
      )
  )
);

const UpdatePaymentDetailsModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "update-payment-details-page-chunk" */
        "./features/subscriptions/features/upgrades/components/UpdatePaymentDetailsModal"
      )
  )
);

const UpgradeSubscriptionModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "upgrade-subscription-page-chunk" */
        "./features/subscriptions/features/upgrades/components/UpgradeSubscriptionModal/UpgradeSubscriptionModal"
      )
  )
);

const SubscriptionUpgradeLoadingModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "subscription-upgrade-loading-page-chunk" */
        "./features/subscriptions/features/upgrades/components/SubscriptionUpgradeLoadingModal"
      )
  )
);

const UpgradeSubscriptionWrapper = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "upgrade-subscription-wrapper-page-chunk" */
        "./features/subscriptions/features/upgrades/components/UpgradeSubscriptionWrapper"
      )
  )
);

const UpgradeUnavailableModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "upgrade-unavailable-page-chunk" */
        "./features/subscriptions/features/upgrades/components/UpgradeUnavailableModal"
      )
  )
);

const SubscriptionInfoModalWebViewiOS = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "subscription-info-modal-webview-ios-page-chunk" */
        "./features/subscriptions/features/webview-ios/SubscriptionInfoModalWebViewiOS"
      )
  )
);

const ConnectMicrosoftAccountModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "connect-microsoft-account-modal" */
        "features/sign-up/components/ConnectMicrosoftAccountModal/ConnectMicrosoftAccountModal"
      )
  )
);

const SessionOverviewModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "session-overview-modal" */
        "./features/classroom/pages/session/features/end-session-modal/components/SessionOverviewModal/SessionOverviewModal"
      )
  )
);

const AmeliaAdModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "amelia-ad-modal" */
        "features/classroom/pages/session/features/end-session-modal/components/AmeliaAdModal/AmeliaAdModal"
      )
  )
);

const AssignmentCompleteModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "assignment-complete-modal" */
        "features/classroom/pages/session/features/end-session-modal/components/AssignmentCompleteModal/AssignmentCompleteModal"
      )
  )
);

const AssignmentsProgressModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "assignments-progress-modal" */
        "features/classroom/pages/session/features/end-session-modal/components/AssignmentsProgressModal/AssignmentsProgressModal"
      )
  )
);

const AwardMemoryModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "award-memory-modal" */
        "features/classroom/pages/session/features/end-session-modal/components/MemoryModal/AwardMemoryModal"
      )
  )
);

const ViewNewMemoryModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "view-new-memory-modal" */
        "features/classroom/pages/session/features/end-session-modal/components/MemoryModal/ViewNewMemoryModal"
      )
  )
);

const ChallengeResultModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "challenge-result-modal" */
        "features/classroom/pages/session/features/end-session-modal/components/ChallengeModal/ChallengeResultModal"
      )
  )
);

const ShareChallengeLinkModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "share-challenge-link-modal" */
        "features/classroom/pages/session/features/end-session-modal/components/ChallengeModal/ShareChallengeLinkModal"
      )
  )
);

const ChangeAvatarModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "change-avatar-modal" */
        "features/avatars/features/avatar-selection/components/AvatarSelectionModal"
      )
  )
);

const PaymentDetailsModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "payment-details-modal" */
        "features/subscriptions/components/UpdatePaymentDetailsModal/components/UpdatePaymentDetailsModal"
      )
  )
);

const SubscriptionCancelModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "subscription-cancel-modal" */
        "features/settings/features/premium/components/SubscriptionCancelModal/SubscriptionCancelModal"
      )
  )
);

const SubscriptionPauseModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "subscription-pause-modal" */
        "features/settings/features/premium/components/SubscriptionPauseModal/SubscriptionPauseModal"
      )
  )
);

const SubscriptionRestartModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "subscription-restart-modal" */
        "features/settings/features/premium/components/SubscriptionRestartModal/SubscriptionRestartModal"
      )
  )
);

const RemoveParentModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "remove-parent-modal" */
        "features/dashboard/features/parents/components/RemoveParentModal"
      )
  )
);

const AddParentModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "add-parent-modal" */
        "features/dashboard/features/parents/components/AddParentModal"
      )
  )
);

const RemoveChildModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "remove-child-modal" */
        "features/parents/pages/settings/components/RemoveChildModal/RemoveChildModal"
      )
  )
);

const ShareChallengeResultModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "share-challenge-result-modal" */
        "features/classroom/pages/session/features/end-session-modal/components/ChallengeModal/ShareChallengeResultModal"
      )
  )
);

const ClassLeaderboardProgressModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "class-leaderboard-progress-modal" */
        "features/classroom/pages/session/features/end-session-modal/components/ClassLeaderboardProgressModal/ClassLeaderboardProgressModal"
      )
  )
);

const EndSessionModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "end-session-modal" */
        "./features/classroom/pages/session/features/end-session-modal/components/EndSessionModal/EndSessionModal"
      )
  )
);

const ExamQuestionsAdModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "exam-questions-ad-modal" */
        "features/classroom/pages/session/features/end-session-modal/components/ExamQuestionsAdModal/ExamQuestionsAdModal"
      )
  )
);

const MiniMockAdModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "mini-mock-ad-modal" */
        "features/classroom/pages/session/features/end-session-modal/components/MiniMockAdModal/MiniMockAdModal"
      )
  )
);

const ParentInviteConfirmationModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "parent-invite-confirmation-modal" */
        "features/classroom/pages/session/features/end-session-modal/components/ParentInviteConfirmationModal/ParentInviteConfirmationModal"
      )
  )
);

const ParentInvitePostAssignmentModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "parent-invite-post-assignment-modal" */
        "features/classroom/pages/session/features/end-session-modal/components/ParentInvitePostAssignmentModal/ParentInvitePostAssignmentModal"
      )
  )
);

const ParentInvitePostXSessionsModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "parent-invite-post-x-sessions-modal" */
        "features/classroom/pages/session/features/end-session-modal/components/ParentInvitePostXSessionsModal/ParentInvitePostXSessionsModal"
      )
  )
);

const PastPapersAdModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "past-papers-ad-modal" */
        "features/classroom/pages/session/features/end-session-modal/components/PastPapersAdModal/PastPapersAdModal"
      )
  )
);

const SelectDailyXpGoalModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "select-daily-xp-goal-modal" */
        "features/classroom/pages/session/features/end-session-modal/components/SelectDailyXpGoalModal/SelectDailyXpGoalModal"
      )
  )
);

const SelectedDailyXpGoalModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "selected-daily-xp-goal-modal" */
        "features/classroom/pages/session/features/end-session-modal/components/SelectedDailyXpGoalModal/SelectedDailyXpGoalModal"
      )
  )
);

const LevelUpModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "level-up-modal" */
        "features/classroom/pages/session/features/end-session-modal/components/LevelUpModal/LevelUpModal"
      )
  )
);

const RewardModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "reward-modal" */
        "features/classroom/pages/session/features/end-session-modal/components/RewardModal/RewardModal"
      )
  )
);

const StudentSentimentModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "student-sentiment-modal" */
        "features/classroom/pages/session/features/end-session-modal/components/StudentSentimentModal/StudentSentimentModal"
      )
  )
);

const WrongAnswersModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "wrong-answers-modal" */
        "features/classroom/pages/session/features/end-session-modal/components/WrongAnswersModal/WrongAnswersModal"
      )
  )
);

const SetDailyXpGoal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "set-daily-xp-goal" */
        "features/dashboard/pages/home-page/features/dailyGoal/SetDailyXpGoal"
      )
  )
);

const EndQuizDailyXpGoalModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "end-quiz-daily-xp-goal-modal" */
        "features/classroom/pages/quiz-session/modals/EndQuizSessionModal/EndQuizDailyXpGoalModal"
      )
  )
);

const EndQuizSelectedDailyXpGoalModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "end-quiz-selected-daily-xp-goal-modal" */
        "features/classroom/pages/quiz-session/modals/EndQuizSessionModal/EndQuizSelectedDailyXpGoalModal"
      )
  )
);

const CompleteStudentAccountModal = lazy(
  () =>
    import(
      /* webpackChunkName: "complete-student-account-page" */ "features/sign-up/complete-account/CompleteStudentAccountModal"
    )
);

const MultiSectionEndSessionFlowModal = lazy(
  () =>
    import(
      /* webpackChunkName: "multi-section-esf" */ "features/classroom/features/end-session-flow/multi-section/MultiSectionEndSessionFlowModal"
    )
);

const CramModeGatingModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "cram-mode-gating-modal-chunk" */
        "features/classroom/pages/multi-section-session/modals/gating/modal/CramModeGatingModal"
      )
  )
);

const AddSubjectsModal = lazy(
  () =>
    import(
      /* webpackChunkName: "add-subjects-modal" */ "features/subjects/modals/add-subjects-modal/AddSubjectsModal"
    )
);

const SectionNotesModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "section-notes-modal" */
        "features/classroom/features/section-notes-modal/SectionNotesModal"
      )
  )
);

const SelectExamBoardModal = lazy(
  () =>
    import(
      /* webpackChunkName: "select-exam-board-modal" */ "features/subjects/modals/select-exam-board-modal/SelectExamBoardModal"
    )
);

const MonthlyPackagePromoModal = lazy(
  () =>
    import(
      /* webpackChunkName: "monthly-package-promo-modal" */ "features/dashboard/pages/home-page/components/MonthlyPackagePromo/MonthlyPackagePromoModal"
    )
);

const ChildNotFoundModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "child-not-found" */
        "features/parents/modals/child-not-found/ChildNotFoundModal"
      )
  )
);

const TranslateLanguageSelectionModal = lazy(
  () =>
    import(
      /* webpackChunkName: "translate-language-selection-modal" */ "features/course-creation/pages/translate-page/components/TranslateLanguageSelectionModal"
    )
);

const UnlockMiniMockPapersModal = lazy(
  () =>
    import(
      /* webpackChunkName: "unlock-mini-mock-papers" */ "features/classroom/pages/exam-questions/pages/mini-mock-pages/modals/unlock-mini-mock-papers-modal/UnlockMiniMockPapersModal"
    )
);

const CreateNewCourseModal = lazy(
  () =>
    import(
      /* webpackChunkName: "create-new-course-modal" */ "features/course-creation/pages/course-creation-page/components/CourseCreationHome/CreateNewCourseModal"
    )
);

const ChildConnectionInviteModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "child-connection-invite-modal" */
        "features/parents/features/child-connection-invite-journey/components/ChildConnectionInviteModal"
      )
  )
);

const ChildConnectionSuccessModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "child-connection-success-modal" */
        "features/parents/features/child-connection-invite-journey/components/ChildConnectionSuccessModal"
      )
  )
);

const ClaimTutoringAdModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "claim-tutoring-ad-modal" */
        "features/parents/modals/claim-tutoring/ClaimTutoringAdModal"
      )
  )
);

const ChildConnectedSuccessModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "child-connected-success-modal" */
        "features/parents/features/child-connected-modal/components/ChildConnectedSuccessModal"
      )
  )
);

const PastPaperGatingModal = lazy(() =>
  loaderWithRetry(
    () =>
      import(
        /* webpackChunkName: "past-paper-gating-modal" */
        "features/classroom/pages/exam-questions/pages/course-exam-questions-homepage/components/ExamPrepHomepage/components/ReviewPastPapers/components/PastPaperGatingModal"
      )
  )
);

export function ModalRoutes() {
  const { state } = useLocation();

  const navigate = useNavigate();

  return (
    <Routes>
      <Route
        path={MODAL_ROUTES.SHARE_ASSIGNMENT.path}
        element={
          <Suspense>
            <ShareAssignmentModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.MANAGE_STUDENT.path}
        element={
          <Suspense>
            <ManageStudentModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.ASSIGNMENT_STUDENT_RESULT.path}
        element={
          <Suspense>
            <AssignmentStudentResultModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.DELETE_CLASS_ASSIGNMENT.path}
        element={
          <Suspense>
            <DeleteClassAssignmentModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.COMPLETE_STUDENT_ACCOUNT.path}
        element={
          <Suspense>
            <CompleteStudentAccountModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.DELETE_CLASS_ASSIGNMENTS.path}
        element={
          <Suspense>
            <DeleteClassAssignmentsModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.CHANGE_AVATAR.path}
        element={
          <Suspense>
            <ChangeAvatarModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.PAYMENT_DETAILS.path}
        element={
          <Suspense>
            <PaymentDetailsModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.SUBSCRIPTION_CANCEL.path}
        element={
          <Suspense>
            <SubscriptionCancelModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.SUBSCRIPTION_PAUSE.path}
        element={
          <Suspense>
            <SubscriptionPauseModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.SUBSCRIPTION_RESTART.path}
        element={
          <Suspense>
            <SubscriptionRestartModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.REMOVE_PARENT.path}
        element={
          <Suspense>
            <RemoveParentModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.ADD_PARENT.path}
        element={
          <Suspense>
            <AddParentModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.REMOVE_CHILD.path}
        element={
          <Suspense>
            <RemoveChildModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.EDIT_MULTI_CLASS_ASSIGNMENT.path}
        element={
          <Suspense>
            <EditMultiClassAssignmentModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.START_SPEEDY_MARKING.path}
        element={
          <Suspense>
            <PreSpeedyMarkingModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.MARKING.path}
        element={
          <Suspense>
            <MarkingModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.INSIGHTS_SECTIONS_SELECTOR.path}
        element={
          <Suspense>
            <SectionSelectorModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.MIS_CLASS_TRIGGER.path}
        element={
          <Suspense>
            <MISClassTriggerModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.REMOVE_COURSE.path}
        element={
          <Suspense>
            <RemoveCourseModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.CHOOSE_REGION.path}
        element={
          <Suspense>
            <RegionSelectionModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.INVITE_PARENT.path}
        element={
          <Suspense>
            <InviteParentModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.INVITE_PARENT_INFO.path}
        element={
          <Suspense>
            <InviteParentInfoModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.INVITE_CLASSES_PARENTS.path}
        element={
          <Suspense>
            <InviteClassesParentsModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.CHANGE_EMAIL.path}
        element={
          <Suspense>
            <ChangeEmailModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.DELETE_CLASS.path}
        element={
          <Suspense>
            <DeleteClassModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.LEAVE_CLASS.path}
        element={
          <Suspense>
            <LeaveClassModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.ARCHIVE_CLASSES.path}
        element={
          <Suspense>
            <ArchiveClassesModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.HIDE_CLASSES.path}
        element={
          <Suspense>
            <HideClassesModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.UNHIDE_CLASSES.path}
        element={
          <Suspense>
            <UnhideClassesModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.PROGRESS_REPORT.path}
        element={
          <Suspense>
            <ProgressReportModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.JOIN_CLASS_STUDENT.path}
        element={
          <Suspense>
            <JoinClassModal navigate={navigate} />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.INVITE_TEACHER_STUDENT.path}
        element={
          <Suspense>
            <InviteTeacherModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.CLASS_DOES_NOT_EXIST.path}
        element={
          <Suspense>
            <ClassDoesNotExistModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.MANAGE_TEACHERS.path}
        element={
          <Suspense>
            <ManageTeachersModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.TRANSFER_OWNERSHIP.path}
        element={
          <Suspense>
            <TransferOwnershipModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.REMOVE_TEACHER.path}
        element={
          <Suspense>
            <RemoveTeacherModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.RESET_PASSWORDS_SCHOOL.path}
        element={
          <Suspense>
            <ResetPasswordsSchoolModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.JOIN_SCHOOL_CLASS.path}
        element={
          <Suspense>
            <JoinSchoolClassModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.JOIN_SCHOOL_CLASSES.path}
        element={
          <Suspense>
            <JoinSchoolClassesModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.CLASS_STUDENT_INVITE_SUCCESS.path}
        element={
          <Suspense>
            <ClassStudentInviteSuccessModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.INVITE_OTHER_CLASSES_PARENTS.path}
        element={
          <Suspense>
            <InviteOtherClassesModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.SCHOOL_RENEWAL_FORM_SUCCESS.path}
        element={
          <Suspense>
            <SchoolRenewalRequestSuccessModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.MIS_EXPIRY.path}
        element={
          <Suspense>
            <TeacherPlatformGatingModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.REVIEW_MCA_MARKING.path}
        element={
          <Suspense>
            <ReviewMultiClassAssignmentMarkingModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.SHARE_SECTION.path}
        element={
          <Suspense>
            <ShareSectionModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.GET_QUIZ_MODE.path}
        element={
          <Suspense>
            <SectionHomepageQuizModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.GET_WRONG_ANSWERS_MODE.TRIAL.path}
        element={
          <Suspense>
            <WrongAnswersTrialModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.GET_WRONG_ANSWERS_MODE.path}
        element={
          <Suspense>
            <GetWrongAnswersModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.DOWNLOAD_NOTES.path}
        element={
          <Suspense>
            <DownloadNotesModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.SECTION_PREVIEW.path}
        element={
          <Suspense>
            <SectionPreviewModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.WORD_PREVIEW.path}
        element={
          <Suspense>
            <WordPreviewModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.PREMIUM_COURSES.path}
        element={
          <Suspense>
            <PremiumCoursesModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.ADD_COURSE_TO_CLASS.path}
        element={
          <Suspense>
            <AddCourseModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.QUIZ_DELETED.path}
        element={
          <Suspense>
            <QuizDeletedModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.QUIZ_ADD_IMAGE_MODAL.path}
        element={
          <Suspense>
            <QuizAddImageModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.QUIZ_PREMIUM_TRIGGER.path}
        element={
          <Suspense>
            <QuizPremiumTrigger />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.QUIZ_SHARE_SYNCED_GATING.path}
        element={
          <Suspense>
            <QuizShareSyncedGatingModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.SHARE_QUIZZES_WITH_TEACHERS.path}
        element={
          <Suspense>
            <ShareQuizzesWithTeachersModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.CHILD_SWITCHER.path}
        element={
          <Suspense>
            <ChildSwitcherModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.TRANSFER_SUBSCRIPTION.path}
        element={
          <Suspense>
            <TransferSubscriptionModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.PARENT_INVITE_CHILDREN.path}
        element={
          <Suspense>
            <ParentInviteChildrenModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.INVITE_TO_CLASS.path}
        element={
          <Suspense>
            <ViewInviteModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.UNARCHIVE_CLASSES.path}
        element={
          <Suspense>
            <UnarchiveClassesModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.CREATE_CLASS.path}
        element={
          <Suspense>
            <CreateClassModalContainer />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.INVITE_STUDENTS_QR_CODE.path}
        element={
          <Suspense>
            <InviteStudentsQrCodeModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.END_QUIZ_SESSION.path}
        element={
          <Suspense>
            <EndQuizSessionAnimation />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.END_QUIZ_SESSION.SUMMARY.path}
        element={
          <Suspense>
            <EndQuizSessionModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.END_QUIZ_SESSION.SET_DAILY_XP_GOAL.path}
        element={
          <Suspense>
            <EndQuizDailyXpGoalModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.END_QUIZ_SESSION.SET_DAILY_XP_GOAL.SUCCESS.path}
        element={
          <Suspense>
            <EndQuizSelectedDailyXpGoalModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.END_QUIZ_SESSION.ASSIGNMENT_COMPLETE.path}
        element={
          <Suspense>
            <QuizSessionAssignmentCompleteModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.END_VOCABULARY_SESSION.path}
        element={
          <Suspense>
            <EndVocabularySessionModal />
          </Suspense>
        }
      >
        <Route
          index
          element={
            <Suspense>
              <EndVocabularySessionSummaryModal />
            </Suspense>
          }
        />
        <Route
          path={
            MODAL_ROUTES.END_VOCABULARY_SESSION.$.ASSIGNMENT_PROGRESS
              .relativePath
          }
          element={
            <Suspense>
              <VocabularyAssignmentProgressModal />
            </Suspense>
          }
        />
        <Route
          path={
            MODAL_ROUTES.END_VOCABULARY_SESSION.$.ASSIGNMENT_COMPLETE
              .relativePath
          }
          element={
            <Suspense>
              <VocabularyAssignmentCompleteModal />
            </Suspense>
          }
        />
      </Route>
      <Route
        path={MODAL_ROUTES.QUIT_QUIZ_SESSION.path}
        element={
          <Suspense>
            <QuitQuizSessionModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.QUIT_SESSION.path}
        element={
          <Suspense>
            <QuitSessionModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.INVITE_PARENTS_EXAM_QUESTIONS.path}
        element={
          <Suspense>
            <InviteParentsForExamQuestionsModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.INVITE_PARENTS_WRONG_ANSWERS.path}
        element={
          <Suspense>
            <InviteParentsForWrongAnswersModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.PARENT_PLATFORM_PREVIEW.path}
        element={
          <Suspense>
            <ParentPlatformPreviewModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.GATE_AMELIA_SECTION.path}
        element={
          <Suspense>
            <AskAmeliaGatingModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.GATE_MULTI_SECTION_WRONG_ANSWER.path}
        element={
          <Suspense>
            <WrongAnswersGatingModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.QUIZ_PREVIEW_MODAL.path}
        element={
          <Suspense>
            <QuizPreviewModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.ASSIGN_UNPUBLISHED_QUIZ_MODAL.path}
        element={
          <Suspense>
            <AssignUnpublishedQuizModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.DELETE_QUIZ_MODAL.path}
        element={
          <Suspense>
            <DeleteQuizModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.PARENT_MARKETING.path}
        element={
          <Suspense>
            <ParentMarketingModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.CLAIM_TUTORING_TRIAL.path}
        element={
          <Suspense>
            <ClaimTutoringTrialModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.COMPLETE_TEACHER_ACCOUNT.path}
        element={
          <Suspense>
            <CompleteTeacherAccountModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.FEEDBACK.path}
        element={
          <Suspense>
            <FeedbackModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.MIS_INFO.path}
        element={
          <Suspense>
            <MISInfoModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.UNMANAGED_USER_IN_SYNCED_SCHOOL.path}
        element={
          <Suspense>
            <UnmanagedUserInSyncedSchoolModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.TEACHER_PARENT_INVITE.PLATFORM_DEMO.path}
        element={
          <Suspense>
            <PlatformDemoModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.TEACHER_PARENT_INVITE.DATA_PROCESSING.path}
        element={
          <Suspense>
            <DataProcessingModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.TEACHER_PARENT_INVITE.SELECT_YEAR_GROUPS.path}
        element={
          <Suspense>
            <SelectYearGroupsModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.TEACHER_PARENT_INVITE.PARENTS_PREVIEW.path}
        element={
          <Suspense>
            <InviteParentsPreviewModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.TEACHER_PARENT_INVITE.SELECT_CLASSES.path}
        element={
          <Suspense>
            <SelectClassesInviteParentsModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.STUDENT_ASSIGNMENT_NOTIFICATIONS.path}
        element={
          <Suspense>
            <ActivateAssignmentNotificationsModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.TUTORING_FORM_JOURNEY.path}
        element={
          <Suspense>
            <TutoringFormModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.MULTI_CLASS_ASSIGNMENTS_GATING.path}
        element={
          <Suspense>
            <UnlockMultiClassAssignmentsModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.SHARE_RESOURCE.path}
        element={
          <Suspense>
            <ShareResourceModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.HARDEST_QUESTIONS_GATING.path}
        element={
          <Suspense>
            <HardestQuestionsGatingModal />
          </Suspense>
        }
      />

      <Route
        path={MODAL_ROUTES.HARDEST_QUESTIONS_TEACHER_GATING.path}
        element={
          <Suspense>
            <HardestQuestionsTeacherGatingModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.UPDATE_APP.path}
        element={
          <Suspense>
            <UpdateAppModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.CONFIRM_REMOVE_STUDENTS.path}
        element={
          <Suspense>
            <ConfirmDeleteStudentsModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.GENERATE_CUSTOM_REPORT.relativePath}
        element={
          <Suspense>
            <GenerateCustomReportModal />
          </Suspense>
        }
      >
        <Route
          path={MODAL_ROUTES.GENERATE_CUSTOM_REPORT.$.TYPE.relativePath}
          element={
            <Suspense>
              <CustomReportTypeStepModal />
            </Suspense>
          }
        />
        <Route
          path={MODAL_ROUTES.GENERATE_CUSTOM_REPORT.$.DETAILS.relativePath}
          element={
            <Suspense>
              <CustomReportDetailsStepModal />
            </Suspense>
          }
        />
        <Route
          path="*"
          index
          element={
            <Navigate
              to={MODAL_ROUTES.GENERATE_CUSTOM_REPORT.$.TYPE.buildRelativePath(
                {}
              )}
              replace
              state={state}
            />
          }
        />
      </Route>
      <Route
        path={MODAL_ROUTES.EXAM_QUESTIONS_INFO.path}
        element={
          <Suspense>
            <ExamQuestionsOnlyInformation />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.MAGIC_QUIZ_INFO.path}
        element={
          <Suspense>
            <MagicQuizzesInfoModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.SET_DAILY_XP_GOAL.path}
        element={
          <Suspense>
            <SetDailyXpGoal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.UPGRADE.path}
        element={<UpgradeSubscriptionWrapper />}
      >
        <Route path={MODAL_ROUTES.UPGRADE.SUBSCRIPTION.path}>
          <Route
            path={MODAL_ROUTES.UPGRADE.SUBSCRIPTION.$.CARD_DETAILS.relativePath}
            element={<UpdatePaymentDetailsModal />}
          />
          <Route
            path={
              MODAL_ROUTES.UPGRADE.SUBSCRIPTION.$.AUTHENTICATION.relativePath
            }
            element={<AuthenticationModal />}
          />
          <Route
            path={
              MODAL_ROUTES.UPGRADE.SUBSCRIPTION.$.RETRY_PAYMENT.relativePath
            }
            element={<RetrySubscriptionPaymentModal />}
          />
          <Route
            path={MODAL_ROUTES.UPGRADE.SUBSCRIPTION.$.SUCCESS.relativePath}
            element={<PaymentSuccessfulModal />}
          />
          <Route
            path={MODAL_ROUTES.UPGRADE.SUBSCRIPTION.$.PREVIEW.relativePath}
            element={<UpgradeSubscriptionModal />}
          />
        </Route>
        <Route index element={<SubscriptionUpgradeLoadingModal />} />
        <Route
          path={MODAL_ROUTES.UPGRADE.$.UNAVAILABLE.relativePath}
          element={<UpgradeUnavailableModal />}
        />
        <Route
          path={MODAL_ROUTES.UPGRADE.$.SUBSCRIPTION_INFO_IOS.relativePath}
          element={<SubscriptionInfoModalWebViewiOS />}
        />
      </Route>
      <Route
        path={`${MODAL_ROUTES.TEACHER.PARENT_AUTO_INVITES.relativePath}/*`}
        element={
          <Routes>
            <Route
              path={
                MODAL_ROUTES.TEACHER.PARENT_AUTO_INVITES.$.PREVIEW.relativePath
              }
              element={
                <Suspense>
                  <ParentAutoInvitesPreviewModal />
                </Suspense>
              }
            />
            <Route
              path={
                MODAL_ROUTES.TEACHER.PARENT_AUTO_INVITES.$.SELECT_YEAR_GROUP
                  .relativePath
              }
              element={
                <Suspense>
                  <ParentAutoInvitesSelectYearGroupModal />
                </Suspense>
              }
            />
            <Route
              path={
                MODAL_ROUTES.TEACHER.PARENT_AUTO_INVITES.$.SUCCESS.relativePath
              }
              element={
                <Suspense>
                  <ParentAutoInvitesSuccessModal />
                </Suspense>
              }
            />
          </Routes>
        }
      />
      <Route
        path={MODAL_ROUTES.CONNECT_MICROSOFT_ACCOUNT.path}
        element={
          <Suspense>
            <ConnectMicrosoftAccountModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.CHILD_NOT_FOUND.path}
        element={
          <Suspense>
            <ChildNotFoundModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.UNLOCK_MINI_MOCK_PAPERS.path}
        element={
          <Suspense>
            <UnlockMiniMockPapersModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.END_SESSION.COURSE.SECTION.path}
        element={
          <Suspense>
            <EndSessionModal />
          </Suspense>
        }
      >
        <Route
          index
          element={
            <Suspense>
              <SessionOverviewModal />
            </Suspense>
          }
        />
        <Route
          path={
            MODAL_ROUTES.END_SESSION.COURSE.SECTION.$.DAILY_XP.SET.relativePath
          }
          element={
            <Suspense>
              <SetDailyXpGoal />
            </Suspense>
          }
        />
        <Route
          path={
            MODAL_ROUTES.END_SESSION.COURSE.SECTION.$.DAILY_XP.SELECT
              .relativePath
          }
          element={
            <Suspense>
              <SelectDailyXpGoalModal />
            </Suspense>
          }
        />
        <Route
          path={
            MODAL_ROUTES.END_SESSION.COURSE.SECTION.$.DAILY_XP.SELECT.SUCCESS
              .relativePath
          }
          element={
            <Suspense>
              <SelectedDailyXpGoalModal />
            </Suspense>
          }
        />
        <Route
          path={MODAL_ROUTES.END_SESSION.COURSE.SECTION.$.LEVEL_UP.relativePath}
          element={
            <Suspense>
              <LevelUpModal />
            </Suspense>
          }
        />
        <Route
          path={MODAL_ROUTES.END_SESSION.COURSE.SECTION.$.REWARD.relativePath}
          element={
            <Suspense>
              <RewardModal />
            </Suspense>
          }
        />
        <Route
          path={
            MODAL_ROUTES.END_SESSION.COURSE.SECTION.$.CLASS_LEADERBOARDS
              .relativePath
          }
          element={
            <Suspense>
              <ClassLeaderboardProgressModal />
            </Suspense>
          }
        />
        <Route
          path={
            MODAL_ROUTES.END_SESSION.COURSE.SECTION.$.ASSIGNMENT_COMPLETE
              .relativePath
          }
          element={
            <Suspense>
              <AssignmentCompleteModal />
            </Suspense>
          }
        />
        <Route
          path={
            MODAL_ROUTES.END_SESSION.COURSE.SECTION.$.PARENT_INVITE.relativePath
          }
          element={
            <Suspense>
              <ParentInvitePostAssignmentModal />
            </Suspense>
          }
        />
        <Route
          path={
            MODAL_ROUTES.END_SESSION.COURSE.SECTION.$.PARENT_INVITE_CONFIRMATION
              .relativePath
          }
          element={
            <Suspense>
              <ParentInviteConfirmationModal />
            </Suspense>
          }
        />
        <Route
          path={
            MODAL_ROUTES.END_SESSION.COURSE.SECTION.$.ASSIGNMENT_PROGRESS
              .relativePath
          }
          element={
            <Suspense>
              <AssignmentsProgressModal />
            </Suspense>
          }
        />
        <Route
          path={
            MODAL_ROUTES.END_SESSION.COURSE.SECTION.$.WRONG_ANSWERS.relativePath
          }
          element={
            <Suspense>
              <WrongAnswersModal />
            </Suspense>
          }
        />
        <Route
          path={
            MODAL_ROUTES.END_SESSION.COURSE.SECTION.$.ASK_AMELIA.relativePath
          }
          element={
            <Suspense>
              <AmeliaAdModal />
            </Suspense>
          }
        />
        <Route
          path={
            MODAL_ROUTES.END_SESSION.COURSE.SECTION.$.MINI_MOCK.relativePath
          }
          element={
            <Suspense>
              <MiniMockAdModal />
            </Suspense>
          }
        />
        <Route
          path={
            MODAL_ROUTES.END_SESSION.COURSE.SECTION.$.EXAM_QUESTIONS
              .relativePath
          }
          element={
            <Suspense>
              <ExamQuestionsAdModal />
            </Suspense>
          }
        />
        <Route
          path={
            MODAL_ROUTES.END_SESSION.COURSE.SECTION.$.PAST_PAPERS.relativePath
          }
          element={
            <Suspense>
              <PastPapersAdModal />
            </Suspense>
          }
        />
        <Route
          path={
            MODAL_ROUTES.END_SESSION.COURSE.SECTION.$.SESSIONS_MILESTONE
              .relativePath
          }
          element={
            <Suspense>
              <ParentInvitePostXSessionsModal />
            </Suspense>
          }
        />
        <Route
          path={
            MODAL_ROUTES.END_SESSION.COURSE.SECTION.$.STUDENT_SENTIMENT
              .relativePath
          }
          element={
            <Suspense>
              <StudentSentimentModal />
            </Suspense>
          }
        />
        <Route
          path={
            MODAL_ROUTES.END_SESSION.COURSE.SECTION.$.AWARD_MEMORY.relativePath
          }
          element={
            <Suspense>
              <AwardMemoryModal />
            </Suspense>
          }
        />
        <Route
          path={
            MODAL_ROUTES.END_SESSION.COURSE.SECTION.$.VIEW_MEMORY.relativePath
          }
          element={
            <Suspense>
              <ViewNewMemoryModal />
            </Suspense>
          }
        />
        <Route
          path={
            MODAL_ROUTES.END_SESSION.COURSE.SECTION.$.CHALLENGE.SHARE
              .relativePath
          }
          element={
            <Suspense>
              <ShareChallengeLinkModal />
            </Suspense>
          }
        />
        <Route
          path={
            MODAL_ROUTES.END_SESSION.COURSE.SECTION.$.CHALLENGE.SHARE_RESULT
              .relativePath
          }
          element={
            <Suspense>
              <ShareChallengeResultModal />
            </Suspense>
          }
        />
        <Route
          path={
            MODAL_ROUTES.END_SESSION.COURSE.SECTION.$.CHALLENGE.RESULT
              .relativePath
          }
          element={
            <Suspense>
              <ChallengeResultModal />
            </Suspense>
          }
        />
      </Route>
      <Route path={MODAL_ROUTES.CREATION.path}>
        <Route
          path={
            MODAL_ROUTES.CREATION.$.COURSE.SECTION.CONTENT.TRANSLATE
              .relativePath
          }
          element={
            <Suspense>
              <TranslateLanguageSelectionModal />
            </Suspense>
          }
        />
        <Route
          path={MODAL_ROUTES.CREATION.$.NEW_COURSE.relativePath}
          element={
            <Suspense>
              <CreateNewCourseModal />
            </Suspense>
          }
        />
      </Route>
      <Route
        path={
          MODAL_ROUTES.END_SESSION.COURSE.MULTI_SECTION.END_SESSION_FLOW
            .relativePath
        }
        element={
          <Suspense>
            <MultiSectionEndSessionFlowModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.CRAM_MODE_GATING.path}
        element={
          <Suspense>
            <CramModeGatingModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.ADD_SUBJECTS.path}
        element={
          <Suspense>
            <AddSubjectsModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.SECTION_NOTES.COURSE.SECTION.path}
        element={
          <Suspense>
            <SectionNotesModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.SELECT_EXAM_BOARD.path}
        element={
          <Suspense>
            <SelectExamBoardModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.MONTHLY_PACKAGE_PROMO.path}
        element={
          <Suspense>
            <MonthlyPackagePromoModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.CHILD_CONNECTION.CONNECT.path}
        element={
          <Suspense>
            <ChildConnectionInviteModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.CHILD_CONNECTION.SUCCESS.path}
        element={
          <Suspense>
            <ChildConnectionSuccessModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.CHILD_CONNECTION.TUTORING_TRIAL.path}
        element={
          <Suspense>
            <ClaimTutoringAdModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.CHILD_CONNECTED_SUCCESS.path}
        element={
          <Suspense>
            <ChildConnectedSuccessModal />
          </Suspense>
        }
      />
      <Route
        path={MODAL_ROUTES.PAST_PAPER_GATING.path}
        element={
          <Suspense>
            <PastPaperGatingModal />
          </Suspense>
        }
      />
    </Routes>
  );
}
